import React, { startTransition, useMemo, useState } from 'react';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Combobox,
    ComboboxItem,
    ComboboxProvider,
    Select,
    SelectItem,
    SelectLabel,
    SelectPopover,
    SelectProvider,
    SelectValue,
} from '@ariakit/react';
import classNames from '../../utils/classNames';
import Input, { INPUT_SIZE } from './Input';
import SuggestionsList, { SUGGESTIONS_POPOVER_BASE_CLASSNAME } from './SuggestionsList';

export type SelectComboboxOption = {
    value: string;
    label: string;
};

export interface SelectComboboxProps {
    options: SelectComboboxOption[];
    value: string;
    onChange: (value: string) => void;
    label?: string;
    disabled?: boolean;
    className?: string;
}

export default function SelectCombobox({
    options,
    value,
    onChange,
    label,
    disabled,
    className,
}: SelectComboboxProps) {
    const [searchValue, setSearchValue] = useState('');
    const optionsByValue = useMemo(
        () => new Map(options.map((option) => [option.value, option])),
        [options]
    );

    const filteredOptions = useMemo(() => {
        const term = searchValue.trim().toLowerCase();
        if (!term) return options;
        return options.filter((option) => option.label.toLowerCase().includes(term));
    }, [options, searchValue]);

    const selectClasses = classNames(
        'focus:shadow-outline relative flex w-full items-center justify-between rounded border border-neutral-300 bg-white px-2 py-4 pr-10 text-left leading-normal focus:outline-none',
        disabled && 'cursor-not-allowed text-neutral-400',
        className
    );

    return (
        <SelectProvider value={value} setValue={(nextValue) => onChange(String(nextValue))}>
            <ComboboxProvider
                value={searchValue}
                resetValueOnHide
                setValue={(nextValue) => {
                    startTransition(() => setSearchValue(nextValue));
                }}
            >
                <div className="relative w-full">
                    {label && (
                        <SelectLabel className="mb-1 block text-sm font-medium text-neutral-700">
                            {label}
                        </SelectLabel>
                    )}
                    <Select className={selectClasses} disabled={disabled}>
                        <SelectValue>
                            {(selectedValue) => {
                                const selected = selectedValue
                                    ? optionsByValue.get(String(selectedValue))
                                    : undefined;
                                const displayValue = selected?.label || gettext('SELECT_OPTION');

                                return (
                                    <span
                                        className={classNames(
                                            'flex-1 truncate',
                                            selected ? 'text-form-text' : 'text-neutral-400'
                                        )}
                                    >
                                        {displayValue}
                                    </span>
                                );
                            }}
                        </SelectValue>
                        <FontAwesomeIcon
                            icon={faAngleDown}
                            className="pointer-events-none absolute right-4 h-4 w-4 text-neutral-500"
                        />
                    </Select>

                    <SelectPopover
                        sameWidth
                        gutter={4}
                        className={SUGGESTIONS_POPOVER_BASE_CLASSNAME}
                    >
                        <div className="border-b border-neutral-200 p-2">
                            <Combobox
                                autoSelect
                                placeholder={gettext('SEARCH')}
                                render={(comboboxProps) => (
                                    <Input
                                        {...comboboxProps}
                                        size={INPUT_SIZE.SMALL}
                                        disabled={disabled}
                                    />
                                )}
                            />
                        </div>
                        <SuggestionsList showEmptyState={filteredOptions.length === 0}>
                            {filteredOptions.map((option) => (
                                <SelectItem
                                    key={option.value}
                                    value={option.value}
                                    className="flex w-full cursor-pointer items-center px-3 py-2 text-left text-neutral-900 hover:bg-neutral-100 data-[active-item]:bg-neutral-100"
                                    render={<ComboboxItem />}
                                >
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SuggestionsList>
                    </SelectPopover>
                </div>
            </ComboboxProvider>
        </SelectProvider>
    );
}
